/**
 * Reassignment Date Bump Logic
 *
 * When a task is reassigned, the due_date is adjusted based on:
 * - Current time relative to 3 PM EST
 * - The task's current due_date relative to today
 *
 * Rules:
 * | Due Date     | Before 3 PM EST | After 3 PM EST |
 * |--------------|-----------------|----------------|
 * | Overdue      | Move to today   | Move to tomorrow |
 * | Today        | No change       | Move to tomorrow |
 * | Future       | No change       | No change        |
 */

import { createConsistentDate } from '@/utils/date';

/**
 * Check if the current time is after 3 PM EST (Eastern Standard Time).
 * Uses America/New_York which handles EST/EDT automatically.
 */
function isAfter3pmEST(): boolean {
  const now = new Date();
  // Get current hour in Eastern time
  const eastern = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric',
    hour12: false,
  }).format(now);
  const easternHour = parseInt(eastern, 10);
  return easternHour >= 15; // 3 PM = 15:00
}

/**
 * Get today and tomorrow as noon-UTC ISO strings (matching createConsistentDate format).
 */
function getTodayAndTomorrow(): { today: string; tomorrow: string } {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  return {
    today: createConsistentDate(today),
    tomorrow: createConsistentDate(tomorrow),
  };
}

/**
 * Compare a due date string to today (date-only comparison).
 * Returns: 'overdue' | 'today' | 'future'
 */
function classifyDueDate(dueDate: string): 'overdue' | 'today' | 'future' {
  const due = new Date(dueDate);
  const now = new Date();

  const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());
  const todayDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (dueDay < todayDay) return 'overdue';
  if (dueDay.getTime() === todayDay.getTime()) return 'today';
  return 'future';
}

export interface DateBumpResult {
  /** Whether the due date should be changed */
  shouldBump: boolean;
  /** The new due date (ISO string), only set if shouldBump is true */
  newDueDate?: string;
  /** Why the bump happened */
  reason?: 'overdue' | 'late_today';
}

/**
 * Determine if a task's due date should be bumped on reassignment,
 * and what the new date should be.
 */
export function computeReassignmentDateBump(
  currentDueDate: string | null | undefined,
): DateBumpResult {
  // No due date — nothing to bump
  if (!currentDueDate) {
    return { shouldBump: false };
  }

  const after3pm = isAfter3pmEST();
  const classification = classifyDueDate(currentDueDate);
  const { today, tomorrow } = getTodayAndTomorrow();

  if (classification === 'overdue') {
    return {
      shouldBump: true,
      newDueDate: after3pm ? tomorrow : today,
      reason: 'overdue',
    };
  }

  if (classification === 'today' && after3pm) {
    return {
      shouldBump: true,
      newDueDate: tomorrow,
      reason: 'late_today',
    };
  }

  // Today before 3pm, or future — no change
  return { shouldBump: false };
}
